import { type GridComparatorFn, type GridColDef } from "@mui/x-data-grid";
import moment from "moment";

import { PublicMaintenance } from "api/public/project/maintenance/maintenance.types";
import { PublicPclPp } from "api/public/project/product/product.types";
import { dataGridDateOnlyGetter } from "components/MUI/DataGrid/DataGrid.utils";
import { PclProduct } from "features/public/Products/PCL/context/PclContextProvider";

import ConformanceClaimsCell from "./Cells/ConformanceClaims.cell";
import FullDetailsCell from "./Cells/FullDetails.cell";
import MaintenanceUpdateCell from "./Cells/MaintenanceUpdate.cell";
import SchemeCell from "./Cells/Scheme.cell";
import StatusCell from "./Cells/Status.cell";

const sortMostRecentMaintenanceDate: GridComparatorFn = (
  value1: PublicMaintenance[],
  value2: PublicMaintenance[],
) => {
  const dates1 = value1.map((d) => moment(d.completion_date));
  const dates2 = value2.map((d) => moment(d.completion_date));

  const newest1 = moment.max(dates1);
  const newest2 = moment.max(dates2);

  return newest1.diff(newest2);
};

export const pclColumns: GridColDef<PclProduct>[] = [
  {
    headerName: "VID",
    field: "v_id",
    sortable: true,
    flex: 0,
    valueFormatter: (value, row) => {
      const { from_cc_portal } = row;
      if (from_cc_portal) return "";
      return value;
    },
  },
  {
    headerName: "Vendor",
    field: "vendor_id_name",
    flex: 1,
    minWidth: 150,
  },
  {
    headerName: "Product",
    field: "product_name",
    flex: 3,
    minWidth: 300,
  },
  {
    headerName: "CCTL",
    field: "assigned_lab_name",
    flex: 1,
    minWidth: 150,
  },
  {
    headerName: "Certification Date",
    field: "certification_date",
    flex: 1,
    minWidth: 140,
    align: "center",
    type: "date",
    valueGetter: dataGridDateOnlyGetter,
  },
  {
    headerName: "Kickoff Date",
    field: "kicked_off_date",
    flex: 1,
    minWidth: 110,
    align: "right",
    type: "date",
    valueGetter: dataGridDateOnlyGetter,
  },
  {
    headerName: "Status",
    field: "status_sort",
    flex: 1,
    minWidth: 120,
    align: "left",
    headerAlign: "center",
    renderCell: ({ row }) => <StatusCell row={row} />,
  },
  {
    headerName: "Conformance Claims",
    field: "protection_profiles",
    sortable: false,
    flex: 1,
    minWidth: 170,
    renderCell: ({ row }) => <ConformanceClaimsCell row={row} />,
    // used for export - does not affect rendering with renderCell
    valueFormatter: (value: PublicPclPp[]) =>
      value.map((pp) => pp.pp_short_name).join(", "),
  },
  {
    headerName: "Tech Types",
    field: "tech_types",
    valueFormatter: (value: string[]) => [...value].sort().join(", "),
  },
  {
    headerName: "Assurance Maintenance Date",
    field: "am_sort",
    flex: 2,
    minWidth: 150,
    align: "center",
    type: "date",
    valueGetter: dataGridDateOnlyGetter,
    valueFormatter: (value, row) => {
      const { status_sort: status } = row;
      if (status === "Archived" || !value) return "";

      return moment.utc(value).format("M/D/YYYY");
    },
  },
  {
    headerName: "Maintenance Update",
    sortable: true,
    field: "active_maintenances",
    flex: 3,
    minWidth: 300,
    renderCell: ({ row }) => <MaintenanceUpdateCell row={row} />,
    // used for export - does not affect rendering with renderCell
    valueFormatter: (value: PublicMaintenance[]) =>
      value
        .map(
          ({ completion_date, title }) =>
            `${moment.utc(completion_date).format("YYYY.MM.DD")}: ${title}`,
        )
        .join(", "),
    sortComparator: sortMostRecentMaintenanceDate,
  },
  {
    headerName: "Scheme",
    field: "submitting_country_id_code",
    flex: 1,
    minWidth: 100,
    align: "center",
    headerAlign: "center",
    renderCell: ({ row }) => (
      <SchemeCell
        countryCode={row.submitting_country_id_code}
        countryName={row.submitting_country_id_code}
      />
    ),
  },
  {
    headerName: "",
    field: "_actions",
    sortable: false,
    flex: 0,
    minWidth: 130,
    renderCell: ({ row }) => <FullDetailsCell row={row} />,
    disableExport: true,
  },
];
